import {currentProjectSelector, projectsSelector} from './selectors/projectSelector';
import ServerActions from 'actions/ServerActions';
import ProjectActions from 'actions/ProjectActions';

export const truncateForChromeStorageLimit = (projects) => (
  window.device.platform === 'web' ? _.take(projects, 100) : projects
);

export const API_SAVE_FREQUENCY = 1000;

export class Repository {
  #store;
  #preferences;
  #previous;

  constructor(store, preferences) {
    this.#store = store;
    this.#preferences = preferences;
    this.#previous = {};
  }

  get store() {
    return this.#store;
  }

  get preferences() {
    return this.#preferences;
  }

  get previous() {
    return this.#previous;
  }

  #persist = () => {
    const {authentication: {token = ""} = {}, userInfo = {}} = _.get(this.store.getState(), 'user', {});
    const {email = ""} = userInfo;
    if (token !== this.previous.token) {
      this.previous.token = token;
      this.preferences.set({key: 'token', value: token});
    }
    if (email !== this.previous.email) {
      this.previous.email = email;
      this.preferences.set({key: 'currentUserEmail', value: email});
    }
    if (userInfo !== this.previous.userInfo) {
      this.previous.userInfo = userInfo;
      this.preferences.set({key: 'userInfo', value: JSON.stringify(userInfo)});
    }
    const {products = []} = this.store.getState();
    if (products !== this.previous.products) {
      this.previous.products = products;
      this.preferences.set({key: 'products', value: JSON.stringify(products)});
    }
  }

  persist = () => {
    this.store.subscribe(this.#persist);
  };

  persistProject = _.debounce(async (remote) => {
    const currentProject = currentProjectSelector(this.store.getState());
    const global_editor = _.get(this.store.getState(), 'user.userInfo.global_editor');

    if (currentProject && (currentProject.owned || global_editor)) {
      if (remote) {
        await this.store.dispatch(ProjectActions.silentUpdateProject(currentProject));
      }

      const projects = projectsSelector(this.store.getState());
      this.preferences.set({
        key: 'projectList',
        value: JSON.stringify(truncateForChromeStorageLimit(projects))
      });
    }
  }, API_SAVE_FREQUENCY);

  loadLocalStorage = async () => {
    await this.preferences.migrate();
    await this.preferences.removeOld();
    const {value: token} = await this.preferences.get({key: 'token'});
    const wasLoggedIn = _.isPresent(token) && token !== 'undefined';
    if (wasLoggedIn) {
      let userInfo = {};
      const {value} = await this.preferences.get({key: 'userInfo'});
      if (value) {
        try {
          userInfo = JSON.parse(value);
        } catch (e) {
          console.warn("Failed to parse userInfo", e);
        }
      }
      const {value: email} = await this.preferences.get({key: 'currentUserEmail'});
      this.store.dispatch(ServerActions.receivedAccessToken(token, email, userInfo));

      const {value: projectList} = await this.preferences.get({key: 'projectList'});
      if (projectList) {
        try {
          this.store.dispatch(ServerActions.receivedProjectList(JSON.parse(projectList) || []));
        } catch (e) {
          console.warn("Failed to parse projectList", e);
        }
      }

      const {value: products} = await this.preferences.get({key: 'products'});
      if (products) {
        try {
          this.store.dispatch({type: 'RESTORE', products: JSON.parse(products)});
        } catch (e) {
          console.warn("Failed to parse products", e);
        }
      }
    }
  };

  syncOfflineProjects = () => {
    const {projects, online} = this.store.getState();
    if (online) {
      _.filter(projects, "deletedOfflineAt").map((project) => {
        this.store.dispatch(ProjectActions.deleteProject(project.id));
      });
      _.filter(projects, "createdOfflineAt").map((project) => {
        this.store.dispatch(ProjectActions.addProject(project));
      });
      _.filter(projects, "updatedOfflineAt").map(({updatedOfflineAt, ...project}) => {
        this.store.dispatch(ProjectActions.updateProject(project));
      });
    }
  };
}

export default Repository;
